import type { JSX } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { Button } from '../components/Button';
import { StoreBadges } from '../components/StoreBadges';
import { PhoneFrame } from '../components/PhoneFrame';
import {
  Eyebrow,
  Footer,
  GetAppBar,
  KindTag,
  PhoneShot,
  TopBar,
  useScrollToHash,
  useSiteTheme,
} from '../components/Site';
import { setTheme } from '../lib/theme';
import changelog from '../content/changelog.json';
import { DEFAULT_DESCRIPTION, DEFAULT_TITLE, usePageMeta } from '../lib/meta';

// Order mirrors the app's shipped locales (en, de, fr, it, es, pt). English
// uses the US flag; the rest use their native endonym.
const LANGS = [
  { flag: '🇺🇸', name: 'English' },
  { flag: '🇩🇪', name: 'Deutsch' },
  { flag: '🇫🇷', name: 'Français' },
  { flag: '🇮🇹', name: 'Italiano' },
  { flag: '🇪🇸', name: 'Español' },
  { flag: '🇵🇹', name: 'Português' },
];

const STATS = [
  { n: '7', label: 'nutrients from every photo' },
  { n: '5', label: 'people per household' },
  { n: '6', label: 'languages' },
  { n: '0', label: 'ads. Ever.' },
];

// Every screen captured for the site (app `make web-shots`), in gallery order.
const SCREENS = [
  { name: 'today', caption: 'Today' },
  { name: 'progress', caption: 'Progress' },
  { name: 'meal', caption: 'A meal, itemised' },
  { name: 'stats', caption: 'Nutrient charts' },
  { name: 'day', caption: 'Any day, in detail' },
  { name: 'nutrition', caption: 'Your targets' },
  { name: 'household', caption: 'Household' },
  { name: 'diary', caption: 'Photo diary' },
];

const FAQ = [
  {
    q: 'Do I need an account?',
    a: 'No. shotmeal works fully offline with no sign-up. An account only adds an optional backup, so your diary follows you to a new phone and to the web.',
  },
  {
    q: 'What does Pro unlock?',
    a: 'Pro unlocks the AI estimates — snap a photo or describe a meal and get calories and nutrients in seconds. Logging by hand, the photo diary, weight tracking, statistics and your household are free.',
  },
  {
    q: 'Which smart scales work?',
    a: 'Any scale whose app saves your weight to Apple Health (iPhone) or Health Connect (Android) — for example Eufy, Withings or Renpho. shotmeal only reads your weight; it never writes to Health.',
  },
  {
    q: 'Is my health data shared?',
    a: 'No. Your weight stays on your phone and in your own optional backup. It is never sold, never used for advertising, and never shown to accounts you link with.',
  },
  {
    q: 'How accurate are the estimates?',
    a: 'Good enough to be useful and honest about it: every estimate lists each item and its portion, and you can fine-tune the number with the dial or fix an item before saving.',
  },
  {
    q: 'Can I track my kids too?',
    a: 'Yes. Add up to four more people on your phone, each with their own diary and goal. For children you set the goal yourself — adult formulas don’t apply to them.',
  },
];

export function Landing() {
  usePageMeta(DEFAULT_TITLE, DEFAULT_DESCRIPTION);
  useScrollToHash();
  return (
    <main class="page-in">
      <TopBar />
      <Hero />
      <StatsBand />
      <Features />
      <Gallery />
      <WhatsNew />
      <Pricing />
      <PrivacyCallout />
      <Faq />
      <LanguageStrip />
      <CTASection />
      <Footer />
      <GetAppBar />
    </main>
  );
}

// ─── Hero ────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section style={{ position: 'relative', overflow: 'hidden' }}>
      <div class="container hero-grid" style={{ padding: '56px 24px 72px' }}>
        <div>
          <a href="/changelog" class="new-pill">
            <span class="new-pill-tag">New</span>
            shotmeal 2.0 — nutrients, weight progress &amp; more
            <span aria-hidden>→</span>
          </a>
          <h1
            class="wordmark"
            style={{
              margin: '18px 0 0',
              fontSize: 'clamp(54px, 9vw, 96px)',
              lineHeight: 0.94,
              letterSpacing: '-0.04em',
              color: 'var(--ink)',
            }}
          >
            Snap a meal,<br />
            know the<br />
            <span style={{ color: 'var(--ember-deep)' }}>calories.</span>
          </h1>
          <p
            style={{
              maxWidth: 520,
              marginTop: 22,
              fontSize: 'clamp(16px, 1.6vw, 19px)',
              color: 'var(--ink-2)',
              lineHeight: 1.5,
            }}
          >
            Point your camera and AI reads the plate — calories, protein,
            carbs and more, in seconds. Follow your weight straight from your
            smart scale, and keep a diary for the whole household.
          </p>
          <div style={{ marginTop: 28 }}>
            <StoreBadges />
          </div>
          <p class="hero-trust">
            Free to start · No account needed · No ads
          </p>
        </div>

        <HeroPhones />
      </div>
    </section>
  );
}

function HeroPhones() {
  return (
    <div class="hero-phones">
      <svg aria-hidden viewBox="0 0 600 600" class="hero-rings">
        <circle cx="300" cy="300" r="240" fill="none" stroke="var(--ember)"
          stroke-opacity="0.4" stroke-width="1.5" stroke-dasharray="3 7" />
        <circle cx="300" cy="300" r="280" fill="none" stroke="var(--ember)"
          stroke-opacity="0.18" stroke-width="1" />
      </svg>
      <div class="hero-side hero-side-left">
        <PhoneFrame width={220} rotate={-8}>
          <PhoneShot name="progress" alt="shotmeal Progress — weight trend, weekly rate and target" />
        </PhoneFrame>
      </div>
      <div class="hero-center">
        <PhoneFrame width={268}>
          <HeroVideo />
        </PhoneFrame>
      </div>
      <div class="hero-side hero-side-right">
        <PhoneFrame width={220} rotate={8}>
          <PhoneShot name="diary" alt="shotmeal photo diary — a week of meals in pictures" />
        </PhoneFrame>
      </div>
    </div>
  );
}

/** A short real tour of the app (Today → Progress → nutrient charts → one
 *  day), muted and looping, recorded in both themes. Falls back to the still
 *  under reduced motion. */
function HeroVideo() {
  const ref = useRef<HTMLVideoElement>(null);
  const theme = useSiteTheme();
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      ref.current?.pause();
    }
  }, [theme]);
  const suffix = theme === 'dark' ? '-dark' : '';
  return (
    <video
      // Re-mount on a theme switch so the matching recording loads.
      key={theme}
      ref={ref}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      poster={theme === 'dark' ? '/shots/today-dark.webp' : '/shots/hero.webp'}
      aria-label="A tour of shotmeal: today's meals, weight progress, nutrient charts and a day's summary"
      style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }}
    >
      <source src={`/shots/hero-tour${suffix}.webm`} type="video/webm" />
      <source src={`/shots/hero-tour${suffix}.mp4`} type="video/mp4" />
    </video>
  );
}

// ─── Numbers ─────────────────────────────────────────────────────────────

function StatsBand() {
  return (
    <section style={{ padding: '0 0 24px' }}>
      <div class="container stats-band">
        {STATS.map((s) => (
          <div key={s.label} class="stat-tile">
            <div class="wordmark stat-n">{s.n}</div>
            <div class="stat-label">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Feature story ───────────────────────────────────────────────────────

function Features() {
  return (
    <section id="features" style={{ padding: '56px 0 24px', scrollMarginTop: 80 }}>
      <div class="container">
        <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 24px' }}>
          <Eyebrow>What it does</Eyebrow>
          <h2 class="wordmark section-title">
            Everything it needs to.<br />
            <span style={{ color: 'var(--ember-deep)' }}>Nothing it doesn't.</span>
          </h2>
        </div>

        <FeatureRow
          eyebrow="Snap"
          title={<>Snap it. Tune it. <span class="accent">Done.</span></>}
          text="Photograph any meal — or just describe it — and AI estimates the calories and nutrients in seconds. Every item is listed with its portion, so you can fix what it got wrong. Spin the dial to fine-tune, and it's in your diary."
          points={[
            'Photo, a quick description or manual entry',
            'Each item with its portion and nutrients',
            'Favourites: your usual breakfast in two taps',
          ]}
          phones={[
            { name: 'camera', dark: false, alt: 'shotmeal AI estimate — a meal photo with the calorie dial' },
            { name: 'meal', alt: 'A meal broken down into items with their nutrients' },
          ]}
        />

        <FeatureRow
          flip
          isNew
          eyebrow="Progress"
          title={<>See where you're <span class="accent">heading.</span></>}
          text="Log your weight by hand, or sync it from Apple Health or Health Connect so your smart scale's readings arrive on their own. shotmeal draws the trend, measures your real weekly rate against the pace you chose, and tells you when you'll reach your target."
          points={[
            'Latest weight, 1 to 24 month charts',
            'Weekly rate vs. your plan — on track or not',
            'Works with Apple Health & Health Connect',
          ]}
          phones={[{ name: 'progress', alt: 'shotmeal Progress — weight chart, weekly rate and target' }]}
        />

        <FeatureRow
          isNew
          eyebrow="Nutrients"
          title={<>More than <span class="accent">calories.</span></>}
          text="Every estimate includes protein, carbs, sugar, fibre, fat, saturated fat and sodium. Pick the ones you care about; targets follow your weight and calorie goal, based on EFSA and WHO reference values — or set your own."
          points={[
            'Daily bars for what you track',
            'Tap any day to see all of it, meal by meal',
            'Targets that update with your profile',
          ]}
          phones={[
            { name: 'nutrition', alt: 'Nutrition settings — tracked nutrients and recommended targets' },
            { name: 'day', alt: 'One day in detail — calories, every nutrient and the meals' },
          ]}
        />

        <FeatureRow
          flip
          isNew
          eyebrow="Household"
          title={<>Made for the whole <span class="accent">household.</span></>}
          text="Track meals for up to five people from one phone — kids included. Link with your partner by QR code to see each other's diaries; weight and body data are never shared."
          points={[
            'One tap to switch between people',
            'Each person with their own goal',
            'Linked diaries, with posting only if you allow it',
          ]}
          phones={[{ name: 'household', alt: 'Household — people on this phone and linked accounts' }]}
        />

        <FeatureRow
          isNew
          eyebrow="Statistics"
          title={<>Stats that <span class="accent">remember.</span></>}
          text="Charts for calories and every nutrient you track, streaks and insights about how you really eat. Change your goal and the past stays honest — each day is judged against the goal you had that day."
          points={[
            'Switch the chart between calories and nutrients',
            'A month at a glance',
            'Insights that actually say something',
          ]}
          phones={[{ name: 'stats', alt: 'Statistics — protein per day against the goal' }]}
        />

        <FeatureRow
          flip
          eyebrow="Diary"
          title={<>A diary in <span class="accent">pictures.</span></>}
          text="Your week, in actual pictures of what you ate. Swipe through every photo you've ever logged, from the first to the last."
          points={['Filter by meal', 'Full-screen photos', 'Totals for every day']}
          phones={[{ name: 'diary', alt: 'Photo diary — a week of meals in pictures' }]}
        />
      </div>
    </section>
  );
}

type Shot = { name: string; alt: string; dark?: boolean };

function FeatureRow({
  eyebrow,
  title,
  text,
  points,
  phones,
  flip,
  isNew,
}: {
  eyebrow: string;
  title: JSX.Element;
  text: string;
  points: string[];
  phones: Shot[];
  flip?: boolean;
  isNew?: boolean;
}) {
  return (
    <div class={`feature-row${flip ? ' is-flipped' : ''}`}>
      <div class="feature-copy">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Eyebrow>{eyebrow}</Eyebrow>
          {isNew && <KindTag kind="new" />}
        </div>
        <h3 class="wordmark feature-title">{title}</h3>
        <p class="feature-text">{text}</p>
        <ul class="feature-points">
          {points.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      </div>
      <div class={`feature-phones n-${phones.length}`}>
        {phones.map((p, i) => (
          <div key={p.name} class="feature-phone">
            <PhoneFrame width={phones.length > 1 ? 236 : 268} rotate={phones.length > 1 ? (i === 0 ? -4 : 4) : 0}>
              <PhoneShot name={p.name} alt={p.alt} dark={p.dark ?? true} />
            </PhoneFrame>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Screen gallery ──────────────────────────────────────────────────────

function Gallery() {
  const theme = useSiteTheme();
  return (
    <section id="screens" style={{ padding: '56px 0', scrollMarginTop: 80 }}>
      <div class="container gallery-head">
        <div>
          <Eyebrow>Screens</Eyebrow>
          <h2 class="wordmark section-title">
            Have a look <span class="accent">around.</span>
          </h2>
        </div>
        <div class="seg" role="group" aria-label="Screenshot theme">
          {(['light', 'dark'] as const).map((t) => (
            <button
              key={t}
              type="button"
              aria-pressed={theme === t}
              class={theme === t ? 'is-on' : ''}
              onClick={() => setTheme(t)}
            >
              {t === 'light' ? 'Light' : 'Dark'}
            </button>
          ))}
        </div>
      </div>
      <div class="gallery" tabIndex={0} aria-label="App screenshots">
        {SCREENS.map((s) => (
          <figure key={s.name} class="gallery-item">
            <PhoneFrame width={236}>
              <PhoneShot name={s.name} alt={`shotmeal — ${s.caption}`} />
            </PhoneFrame>
            <figcaption>{s.caption}</figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

// ─── What's new ──────────────────────────────────────────────────────────

function WhatsNew() {
  const latest = changelog.releases[0];
  return (
    <section style={{ padding: '32px 0 56px' }}>
      <div class="container">
        <div class="whats-new">
          <div class="whats-new-head">
            <div>
              <div class="whats-new-eyebrow">What's new · {latest.version}</div>
              <h2 class="wordmark" style={{ margin: '8px 0 0', fontSize: 'clamp(30px, 4.4vw, 48px)', lineHeight: 1.05, color: 'var(--hero-fg)' }}>
                {latest.title}.
              </h2>
              <p style={{ margin: '10px 0 0', color: 'var(--hero-fg-soft)', maxWidth: 520, lineHeight: 1.5 }}>
                {latest.summary}
              </p>
            </div>
            <a href="/changelog" class="whats-new-link">Full changelog →</a>
          </div>
          <div class="whats-new-grid">
            {latest.highlights.slice(0, 4).map((h) => (
              <div key={h.title} class="whats-new-card">
                <KindTag kind={h.kind as 'new' | 'improved' | 'fixed'} />
                <div class="wordmark" style={{ fontSize: 24, lineHeight: 1.05, marginTop: 10, color: 'var(--hero-fg)' }}>
                  {h.title}
                </div>
                <p style={{ margin: '6px 0 0', color: 'var(--hero-fg-soft)', fontSize: 14, lineHeight: 1.5 }}>
                  {h.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Pricing ─────────────────────────────────────────────────────────────

function Pricing() {
  return (
    <section id="pricing" style={{ padding: '32px 0 56px', scrollMarginTop: 80 }}>
      <div class="container">
        <div style={{ textAlign: 'center' }}>
          <Eyebrow>Pricing</Eyebrow>
          <h2 class="wordmark section-title">
            Free to use. <span class="accent">Pro when you want AI.</span>
          </h2>
        </div>
        <div class="pricing-grid">
          <PriceCard
            name="Free"
            price="$0"
            note="forever, no account needed"
            items={[
              'Log meals by hand, with photos',
              'Photo diary and statistics',
              'Weight tracking & Apple Health / Health Connect',
              'Household of up to 5 people',
              'Optional backup across devices',
            ]}
          />
          <PriceCard
            featured
            name="Pro"
            price="$5"
            per="/ month"
            note="or $20 / year · 7-day free trial"
            items={[
              'Everything in Free',
              'AI estimates from a photo or a description',
              'Protein, carbs, sugar and more on every estimate',
              'Estimate later when you were offline',
            ]}
          />
        </div>
        <p style={{ textAlign: 'center', color: 'var(--ink-3)', fontSize: 13, marginTop: 16 }}>
          Prices in USD; your store shows the price for your country.
        </p>
      </div>
    </section>
  );
}

function PriceCard({
  name,
  price,
  per,
  note,
  items,
  featured,
}: {
  name: string;
  price: string;
  per?: string;
  note: string;
  items: string[];
  featured?: boolean;
}) {
  return (
    <div class={`price-card${featured ? ' is-featured' : ''}`}>
      <div class="price-name">{name}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 6 }}>
        <span class="wordmark price-amount">{price}</span>
        {per && <span class="price-per">{per}</span>}
      </div>
      <div class="price-note">{note}</div>
      <ul class="feature-points" style={{ marginTop: 18 }}>
        {items.map((i) => (
          <li key={i}>{i}</li>
        ))}
      </ul>
    </div>
  );
}

// ─── Privacy, FAQ, languages, CTA ────────────────────────────────────────

function PrivacyCallout() {
  return (
    <section style={{ padding: '24px 0 48px' }}>
      <div class="container">
        <div class="privacy-card">
          <div class="privacy-eyebrow">Privacy</div>
          <div class="wordmark" style={{ fontSize: 'clamp(28px, 4vw, 44px)', lineHeight: 1.1, color: 'var(--hero-fg)' }}>
            Your meals and your weight stay yours{' '}
            <span style={{ color: 'var(--ember)' }}>unless you opt in.</span>
          </div>
          <p style={{ maxWidth: 640, color: 'var(--hero-fg-soft)', lineHeight: 1.5, fontSize: 16, margin: 0 }}>
            shotmeal works offline by default. Sign in only if you want a
            backup across devices. We never sell your data, never train on
            your meals, and never show your weight to anyone — not even the
            people you link with.
          </p>
        </div>
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section id="faq" style={{ padding: '24px 0 48px', scrollMarginTop: 80 }}>
      <div class="container" style={{ maxWidth: 820 }}>
        <div style={{ textAlign: 'center' }}>
          <Eyebrow>Questions</Eyebrow>
          <h2 class="wordmark section-title">Good to know.</h2>
        </div>
        <div class="faq">
          {FAQ.map((f) => (
            <details key={f.q}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function LanguageStrip() {
  return (
    <section style={{ padding: '8px 0 40px' }}>
      <div class="container" style={{ textAlign: 'center' }}>
        <Eyebrow>Speaks your language</Eyebrow>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 12,
            marginTop: 20,
          }}
        >
          {LANGS.map((l) => (
            <div key={l.name} class="lang-chip">
              <span style={{ fontSize: 18, lineHeight: 1 }} aria-hidden>
                {l.flag}
              </span>
              <span>{l.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section style={{ padding: '48px 0 96px' }}>
      <div class="container" style={{ textAlign: 'center' }}>
        <Eyebrow>Ready when you are</Eyebrow>
        <h2 class="wordmark section-title">Snap your first meal in 30 seconds.</h2>
        <div
          style={{
            marginTop: 28,
            display: 'inline-flex',
            flexDirection: 'column',
            gap: 16,
            alignItems: 'center',
          }}
        >
          <StoreBadges />
          <Button href="/auth" variant="ghost" size="md">
            or sign in to see your diary on the web →
          </Button>
        </div>
      </div>
    </section>
  );
}

